import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const MSG =
  "Si sales ahora se perder" + String.fromCharCode(0xe1) + " la informaci" + String.fromCharCode(0xf3) +
  "n que has rellenado. " + String.fromCharCode(0xbf) + "Seguro que quieres salir?";

export function useAvisoSalida(when) {
  const nav = useNavigate();
  const whenRef = useRef(when);
  const libre = useRef(false);
  const destRef = useRef(null);
  const blockerRef = useRef(null);
  const forzado = useRef(false);
  const [saliendo, setSaliendo] = useState(false);
  whenRef.current = when;

  const shouldBlock = useCallback(({ currentLocation, nextLocation }) => {
    if (libre.current || !whenRef.current) return false;
    return currentLocation.pathname !== nextLocation.pathname;
  }, []);

  const blocker = useBlocker(shouldBlock);
  blockerRef.current = blocker;

  useEffect(() => {
    if (blocker.state === "blocked" && blocker.location) {
      destRef.current = blocker.location;
    }
  }, [blocker]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (libre.current || !whenRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const irADestino = useCallback((dest) => {
    if (!dest || forzado.current) return;
    const to = `${dest.pathname}${dest.search || ""}${dest.hash || ""}`;
    if (window.location.pathname === dest.pathname && window.location.search === (dest.search || "")) {
      return;
    }
    forzado.current = true;
    nav(to, { replace: true });
  }, [nav]);

  useEffect(() => {
    if (!libre.current || blocker.state !== "blocked" || forzado.current) return;
    const dest = destRef.current || blocker.location;
    try { blocker.reset(); } catch (_) { /* ignore */ }
    irADestino(dest);
  }, [blocker, irADestino]);

  const liberar = () => {
    libre.current = true;
  };

  const confirmar = () => {
    libre.current = true;
    setSaliendo(true);
    const dest = destRef.current || (blocker.state === "blocked" ? blocker.location : null);
    destRef.current = dest;
    if (blocker.state === "blocked") {
      try { blocker.proceed(); } catch (_) { /* ignore */ }
    }
    window.setTimeout(() => {
      const d = destRef.current;
      if (!d || window.location.pathname === d.pathname) return;
      const b = blockerRef.current;
      if (b && b.state === "blocked") {
        try { b.reset(); } catch (_) { /* ignore */ }
      }
      irADestino(d);
    }, 80);
  };

  const cancelar = () => {
    setSaliendo(false);
    if (blocker.state === "blocked") blocker.reset();
  };

  return { blocker, saliendo, liberar, confirmar, cancelar };
}

export default function AvisoSalida({ blocker, saliendo, confirmar, cancelar, mensaje }) {
  const abierto = blocker?.state === "blocked" && !saliendo;

  useEffect(() => {
    if (!abierto) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && cancelar) cancelar();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [abierto, cancelar]);

  if (!abierto) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/80"
        aria-label="Seguir editando"
        onClick={() => cancelar && cancelar()}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="aviso-salida-titulo"
        aria-describedby="aviso-salida-desc"
        data-testid="aviso-salida"
        className="relative z-10 w-full max-w-lg gap-4 rounded-lg border bg-background p-6 shadow-lg"
      >
        <div className="flex flex-col space-y-2 text-center sm:text-left">
          <h2 id="aviso-salida-titulo" className="text-lg font-semibold">
            Salir sin guardar
          </h2>
          <p id="aviso-salida-desc" className="text-sm text-muted-foreground">
            {mensaje || MSG}
          </p>
        </div>
        <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
          <Button type="button" variant="outline" onClick={() => cancelar && cancelar()}>
            Seguir editando
          </Button>
          <Button
            type="button"
            className="bg-blue-700 hover:bg-blue-800"
            onClick={() => confirmar && confirmar()}
          >
            Salir
          </Button>
        </div>
      </div>
    </div>
  );
}
